import { useParams } from 'react-router-dom'
import { Avatar, Tooltip } from 'antd'
import cn from 'clsx'
import styles from './RequestAML.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import { ProfileRole, useGetTeam } from 'modules/users'
import {
  Block,
  Modal,
  Button,
  Box,
  Text,
  UserAvatar,
  Loader,
  Tag,
  IconTitle,
  IconButton,
  ShieldChekFifthIcon,
  ArrowIcon,
  CertificateChekIcon,
  CloseFillIcon,
} from 'common/components'
import { useOpen } from 'common/hooks'
import {
  AMLBeneficiaryForm,
  AMLCheckDetails,
  useGetRequest,
  useGetAML,
  isMilestonePassed,
} from '..'

interface AMLListProps {
  request: any
  items: any[]
}

interface AMLItemProps {
  role: ProfileRole
  request: any
  item: any
}

export default function RequestAML() {
  const params = useParams()
  // const role = useStore((state) => state.role)
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const { data: request } = useGetRequest(params.uuid)
  const isChecked = isMilestonePassed('aml-check-initiated', request)
  const { data: aml } = useGetAML(request?.project?.uuid, !!request?.project?.uuid && isChecked)
  const isAMLBeneficiaryOpen = useStore((state) => state.isAMLBeneficiaryOpen)
  const setAMLBeneficiaryOpen = useStore((state) => state.setAMLBeneficiaryOpen)

  const handleAMLBeneficiaryOpen = () => setAMLBeneficiaryOpen(true)

  const handleAMLClose = () => setAMLBeneficiaryOpen(false)

  const buttonAdd =
    request.status === 'approved' || request.status === 'aml-check' ? (
      <Button variant="secondary" onClick={handleAMLBeneficiaryOpen}>
        add beneficiary
      </Button>
    ) : null

  return (
    <>
      <Block className={styles.root} type="white">
        <div className={styles.block}>
          <IconTitle icon={<ShieldChekFifthIcon />}>AML check </IconTitle>
          {(!isChecked) &&
            (!!permissions?.includes('add beneficiary')) &&
            buttonAdd
          }
        </div>
        {isChecked && (
          <>
            <div className={styles.header}>
              <Text color="grey">
                Must be verified by Loans Coordinator or Financial Controller
              </Text>
              <Avatars />
            </div>
            {aml ? <AMLList request={request} items={aml} /> : <Loader />}
            {(!!permissions?.includes('add beneficiary')) &&
              ['approved', 'aml-check'].includes(request.status) &&
              buttonAdd
            }
          </>
        )}
      </Block>
      <Modal
        width={486}
        title="AML beneficiary"
        open={isAMLBeneficiaryOpen}
        onCancel={handleAMLClose}
      >
        <AMLBeneficiaryForm request={request} onClose={handleAMLClose} />
      </Modal>
    </>
  )
}

const Avatars = () => {
  const { data: team } = useGetTeam()

  if (!team) return null

  const coordinatorsControllers = team.data?.filter(
    (user: any) => user.role === 'controller' || user.role === 'coordinator'
  )

  return (
    <Avatar.Group
      maxCount={coordinatorsControllers.length <= 4 ? undefined : 3}
      size={48}
    >
      {coordinatorsControllers.map((user: any) => (
        <UserAvatar
          key={user.uuid}
          size={48}
          src={user.photo}
          alt={user.firstName}
        />
      ))}
    </Avatar.Group>
  )
}

const AMLList = ({ request, items }: AMLListProps) => {
  const role = useStore((state) => state.role)

  return (
    <ul className={styles.list}>
      {items.map((item) => (
        <AMLItem key={item.uuid} role={role} request={request} item={item} />
      ))}
    </ul>
  )
}

const AMLItem = ({ role, request, item }: AMLItemProps) => {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const [isOpen, handleOpen, handleClose] = useOpen()
  const isGreenID = item.type === 'green-id'

  return (
    <>
      <li className={styles.item}>
        <Tooltip title={item.name}>
          <span className={cn(styles.beneficiary_name)}>{item.name}</span>
        </Tooltip>
        <p>
          {isGreenID ? 'Green ID' : 'Manual check'}
          <Text as="span" color="grey">
            {isGreenID ? (
              item.greenIdStatus === 'pending' ? (
                ': pending...'
              ) : item.greenIdStatus === 'approved' ? (
                <CertificateChekIcon className={styles.statusIcon} />
              ) : (
                <CloseFillIcon className={styles.statusIcon} />
              )
            ) : item.locked ? null : (
              <p>in progress...</p>
            )}
          </Text>
        </p>
        <Box align="right">
          {item.status === 'approved' ? (
            <Tag block icon="check" color="green" onClick={handleOpen}>
              Approved
            </Tag>
          ) : item.status === 'rejected' ? (
            <Tag block icon="close" color="red" onClick={handleOpen}>
              Rejected
            </Tag>
          ) : role.director ? null :
            (
              (!!permissions?.includes('aml check')) &&
              (isGreenID ? item.greenIdStatus !== 'pending' : item.locked)
            )
              ? (
                <Button block className={styles.button} onClick={handleOpen}>
                  Check
                </Button>
              )
              : (
                <IconButton
                  icon={<ArrowIcon className={styles.icon} />}
                  onClick={handleOpen}
                />
              )
          }
        </Box>
      </li>
      {request.status === 'approved' ? (
        <Modal
          width={486}
          title="AML beneficiary"
          open={isOpen}
          onCancel={handleClose}
        >
          <AMLBeneficiaryForm
            item={item}
            request={request}
            onClose={handleClose}
          />
        </Modal>
      ) : (
        <Modal
          width={486}
          title={
            <div className={styles.modalTitle}>
              <p>AML check</p>
            </div>
          }
          open={isOpen}
          onCancel={handleClose}
        >
          <AMLCheckDetails request={request} aml={item} onClose={handleClose} />
        </Modal>
      )}
    </>
  )
}
